import React, { useState } from "react";
import { Eye, EyeOff, ArrowRight, Lock } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const redirect = location.state?.from?.pathname;
      if (redirect) navigate(redirect, { replace: true });
      else navigate(u.role === "admin" ? "/admin" : "/portal", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="login-page">
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('/images/login-bg.jpg')" }} />
      <div className="absolute inset-0 backdrop-blur-sm bg-black/75" />
      <div className="absolute top-0 left-0 right-0 h-px gold-gradient" />
      <div className="absolute bottom-0 left-0 right-0 h-px gold-gradient" />
      <div className="absolute top-8 left-8 w-12 h-12 border-t border-l border-gold/30" />
      <div className="absolute top-8 right-8 w-12 h-12 border-t border-r border-gold/30" />
      <div className="absolute bottom-8 left-8 w-12 h-12 border-b border-l border-gold/30" />
      <div className="absolute bottom-8 right-8 w-12 h-12 border-b border-r border-gold/30" />

      <Link to="/" className="absolute top-6 left-6 nav-link text-[0.65rem] flex items-center gap-2 z-10">
        <span className="text-gold/60">←</span>
        <span>Back to Home</span>
      </Link>

      <div className="relative z-10 w-full max-w-md px-6 animate-fade-in-up">
        <div className="glass-dark rounded-sm p-10 md:p-12">
          <div className="text-center mb-10">
            <img
              src="/images/mrva-logo-white.png"
              alt="MRVA Productions"
              className="h-20 md:h-24 w-auto object-contain mx-auto mb-4"
            />
            <div className="font-body text-gold text-[0.6rem] tracking-[0.4em] uppercase">Client Portal</div>
            <div className="gold-divider mt-6" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="input-gold w-full px-4 py-3.5 rounded-sm text-sm font-body"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-gold w-full px-4 py-3.5 pr-12 rounded-sm text-sm font-body"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-gold transition-colors"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3" data-testid="login-error">
                <p className="font-body text-red-400 text-xs leading-relaxed">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full py-4 text-xs rounded-sm flex items-center justify-center gap-3 mt-2"
              data-testid="login-submit"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  <span>Authenticating...</span>
                </div>
              ) : (
                <>
                  <span>Enter Portal</span>
                  <ArrowRight size={14} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/signup" className="font-body text-white/40 text-xs tracking-wider hover:text-gold transition-colors">
              Don't have an account? <span className="text-gold">Create one</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
